// Libraries import
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";

// Styles and Assets import
import { ReactComponent as TimeIcon } from "../../../assets/images/time.svg";

// Api, Utils and Constants
import * as Api from "../../../api";
import { updateRestaurantData } from "../../../redux/actions/userDataActions";
import { THEME_MODE } from "../../../constants/Theme";

// Components
import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import SettingTextField from "../../../components/SettingTextField";
import AlertMsg from "../../../components/AlertMsg";
import RestaurantHeader from "../../../components/RestaurantHeader";

const Field = {
    delivery: "deliveryEta",
    dining: "diningEta",
}

const OrderTimeSettings = ({ themeMode, restaurantData, updateRestaurantData }) => {
    const theme_mode = THEME_MODE[themeMode];
    const themeStyles = ComponentStyles(theme_mode);

    const navigate = useNavigate();
    const { rest_id } = useParams();

    const [loading, setLoading] = useState(true);
    const [deliveryETA, setDeliveryETA] = useState("");
    const [diningETA, setDiningETA] = useState("");
    const [msgAlert, setMsgAlert] = useState({
        open: false,
        message: "",
        msgType: "error",
    });
    const [errors, setErrors] = useState(new Map());
    const [editable, setEditable] = useState(false);
    const [saveBtnDisabled, setSaveBtnDisabled] = useState(true);

    useEffect(() => getRestaurantData(), []);

    const getRestaurantData = () => {
        setLoading(true);
        Api.getRestaurantData(restaurantData._id).then((response) => {
            if (response.success) {
                const data = response.data;
                setDeliveryETA(data.deliveryETA);
                setDiningETA(data.diningETA);
                updateRestaurantData(data);
            } else {
                setMsgAlert({ open: true, message: response.msg, msgType: "error" });
            }
            setLoading(false);
        });
    };

    const handleChange = (field) => (val) => {
        val = val.replace(/[^0-9]/g, "");
        if (field === Field.delivery)
            setDeliveryETA(val);
        else setDiningETA(val);

        const newErrors = new Map(errors);
        if (!val) newErrors.set(field, `${field === Field.delivery ? "Delivery" : "Dining"} ETA Required`);
        else newErrors.delete(field);
        setErrors(newErrors);

        const delValue = field === Field.delivery ? val : deliveryETA;
        const dinValue = field === Field.dining ? val : diningETA;
        if (delValue == restaurantData.deliveryETA && dinValue == restaurantData.diningETA) setSaveBtnDisabled(true);
        else setSaveBtnDisabled(false);
    }

    const onEditSettings = () => setEditable(!editable);

    const onSaveGeneralSettings = () => {
        if (errors.size) return;

        const serverData = {
            deliveryETA,
            diningETA,
        };

        setLoading(true);
        Api.updateRestaurant(serverData, restaurantData._id).then((response) => {
            if (response.success) {
                updateRestaurantData({
                    ...restaurantData,
                    deliveryETA,
                    diningETA
                });
                setEditable(false);
                setSaveBtnDisabled(true);
                setMsgAlert({ open: true, message: response.msg, msgType: "success" });
            } else {
                setMsgAlert({ open: true, message: response.msg, msgType: "error" });
            }
            setLoading(false);
        });
    };

    return (
        <Container
            page={"restaurants"}
            onSearch={() => null}
            searchVal=""
            searchLabel=""
        >
            {loading ? <Box sx={{
                width: "100%",
                height: "calc(100vh - 4rem)", // Height of the search bar at the top
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}><Loader />
            </Box> : (
                <Grid container xs={12}>
                    <Grid item xs={12} mb={2}>
                        <RestaurantHeader
                            title={"Order Time Settings"}
                            onBackBtnClick={() => navigate("/restaurant/" + rest_id)}
                        />
                    </Grid>

                    <Box container xs={12} px={3} py={2} sx={themeStyles.mainView}>
                        {/* <Grid container xs={12} md={10} lg={8} xl={6}> */}
                        <Box>
                            <Grid item xs={12} md={10} lg={8} xl={6}>
                                <Box mb={1}>
                                    <Typography sx={themeStyles.textInputLabel}>
                                        Delivery ETA
                                    </Typography>
                                </Box>
                                <Box>
                                    <SettingTextField
                                        editable={editable}
                                        value={deliveryETA}
                                        setValue={handleChange(Field.delivery)}
                                        placeholder={""}
                                        required={true}
                                        variant={"filled"}
                                        border={`1px solid ${theme_mode.inputLabel}`}
                                        leftView={<TimeIcon width={20} height={20} />}
                                        rightView={
                                            <Typography sx={themeStyles.minutesText}>
                                                minutes
                                            </Typography>
                                        }
                                        error={errors.get(Field.delivery)}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={10} lg={8} xl={6} mt={3}>
                                <Box mb={1}>
                                    <Typography sx={themeStyles.textInputLabel}>
                                        Pickup ETA
                                    </Typography>
                                </Box>
                                <Box>
                                    <SettingTextField
                                        editable={editable}
                                        value={diningETA}
                                        setValue={handleChange(Field.dining)}
                                        placeholder={""}
                                        required={true}
                                        variant={"filled"}
                                        border={`1px solid ${theme_mode.inputLabel}`}
                                        leftView={<TimeIcon width={20} height={20} />}

                                        rightView={
                                            <Typography sx={themeStyles.minutesText}>
                                                minutes
                                            </Typography>
                                        }
                                        error={errors.get(Field.dining)}
                                    />
                                </Box>
                            </Grid>

                            <Grid container item xs={12} md={10} lg={8} xl={6} mt={5}>
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: "flex-end" }}>
                                    <Button
                                        type="button"
                                        sx={{
                                            ...themeStyles.btn,
                                            ...themeStyles.editBtn,
                                            flex: 1,
                                        }}
                                        onClick={onEditSettings}
                                    >
                                        {editable ? "Cancel" : "Edit"}
                                    </Button>

                                    <Button
                                        type="button"
                                        disabled={saveBtnDisabled}
                                        sx={{
                                            ...themeStyles.btn,
                                            ...themeStyles.saveBtn,
                                            flex: 1,
                                            backgroundColor: saveBtnDisabled
                                                ? theme_mode.btnDisabledBg
                                                : theme_mode.btnBg4,
                                        }}
                                        onClick={onSaveGeneralSettings}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Grid>
            )
            }
            <AlertMsg
                msgAlert={msgAlert}
                onCloseAlertMsg={() =>
                    setMsgAlert({ ...msgAlert, open: false })
                }
            />
        </Container >
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        updateRestaurantData: (data) => dispatch(updateRestaurantData(data)),
    };
};

const mapStateToProps = (state) => {
    return {
        themeMode: state.userData.themeMode,
        restaurantData: state.userData.restaurantData,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderTimeSettings);

const ComponentStyles = (Theme) => ({
    mainView: {
        width: "100%",
        backgroundColor: "#ffffff",
        minHeight: "calc(100vh - 180px)",
        borderRadius: "8px"
    },
    textInputLabel: {
        fontSize: "0.90625rem",
        fontFamily: "GilroySemiBold !important",
    },
    minutesText: {
        fontsize: "0.875rem",
        fontFamily: "GilroySemiBold !important",
        color: Theme.minutesText,
    },
    btn: {
        cursor: "pointer !important",
        textTransform: "uppercase !important",
        fontSize: "15px !important",
        height: "44px",
        width: "100%",
        minWidth: '14rem',
        maxWidth: '14rem',
        fontFamily: "GilroySemiBold !important",
        borderRadius: "9px !important",
    },
    saveBtn: {
        backgroundColor: Theme.btnBg4,
        color: Theme.btnText,
        ml: 1,
        "&:hover": {
            backgroundColor: Theme.btnBg4 + " !important",
        },
    },
    editBtn: {
        backgroundColor: Theme.btnBg5,
        border: "2px solid " + Theme.btnBg5Border,
        color: Theme.text,
        mr: 1,
        "&:hover": {
            backgroundColor: Theme.btnBg5 + " !important",
        },
    },

    btnMainView: {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row"
    },

});
